/**
 * API Client Utility
 * Handles all external API communications
 */

import axios from 'axios';
import { config } from '../config/config.js';
import { supabase } from '../lib/supabase';

// Create axios instance with default configuration
const apiClient = axios.create({
  timeout: config.api.timeout,
  headers: config.api.baseHeaders
});

// Request interceptor for adding authentication (logging disabled for production)
apiClient.interceptors.request.use(
  (config) => {
    // // HIPAA Compliance: Removed console logging
return config;
  },
  (error) => {
    // // HIPAA Compliance: Removed console logging
return Promise.reject(error);
  }
);

// Response interceptor for error handling (logging disabled for production)
apiClient.interceptors.response.use(
  (response) => {
    // // HIPAA Compliance: Removed console logging
return response;
  },
  (error) => {
    // // HIPAA Compliance: Removed console logging
return Promise.reject(error);
  }
);

/**
 * Retry mechanism for failed requests
 */
const retryRequest = async (requestFunction, maxRetries = config.api.retryAttempts) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await requestFunction();
    } catch (error) {
      
      if (attempt === maxRetries) {
        throw error;
      }
      
      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, config.api.retryDelay * attempt));
    }
  }
};

/**
 * AI Analysis Service Integration
 */
export const aiAnalysisService = {
  /**
   * Compress image if it's larger than specified size
   * @param {string} imageBase64 - Base64 image data
   * @param {number} maxSizeMB - Maximum size in MB (default: 6)
   * @param {number} quality - JPEG quality (0-1, default: 0.85)
   * @returns {Promise<string>} Compressed base64 image
   */
  compressImage: async (imageBase64, maxSizeMB = 6, quality = 0.85) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      // Add timeout to prevent hanging
      const timeout = setTimeout(() => {
        reject(new Error('Image compression timeout'));
      }, 30000); // 30 second timeout

      img.onload = () => {
        try {
          clearTimeout(timeout);

          // Calculate current size
          const b64 = imageBase64.includes(',') ? imageBase64.split(',')[1] : imageBase64;
          const currentSizeMB = (b64.length * 3) / 4 / (1024 * 1024);

          if (currentSizeMB <= maxSizeMB) {
            // No compression needed
            resolve(imageBase64);
            return;
          }

          let { width, height } = img;

          // For very large images, start with more aggressive resizing
          if (currentSizeMB > 10) {
            // Scale down large images first to prevent memory issues
            const targetRatio = Math.sqrt(maxSizeMB / currentSizeMB) * 0.8; // Be more aggressive
            width = Math.floor(width * targetRatio);
            height = Math.floor(height * targetRatio);
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          // Try aggressive quality levels to ensure we get under 6MB
          const qualityLevels = [0.85, 0.70, 0.55, 0.40, 0.25];
          let compressedBase64 = null;
          let finalSizeMB = currentSizeMB;

          for (const testQuality of qualityLevels) {
            compressedBase64 = canvas.toDataURL('image/jpeg', testQuality);
            finalSizeMB = (compressedBase64.split(',')[1].length * 3) / 4 / (1024 * 1024);

            if (finalSizeMB <= maxSizeMB) {
              break;
            }
          }

          // If still too large, progressively resize
          while (finalSizeMB > maxSizeMB && (width > 200 || height > 200)) {
            width = Math.floor(width * 0.85);
            height = Math.floor(height * 0.85);

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            compressedBase64 = canvas.toDataURL('image/jpeg', 0.70);
            finalSizeMB = (compressedBase64.split(',')[1].length * 3) / 4 / (1024 * 1024);
          }

          // Final fallback - use lowest quality if still too large
          if (finalSizeMB > maxSizeMB) {
            compressedBase64 = canvas.toDataURL('image/jpeg', 0.15);
          }

          resolve(compressedBase64);
        } catch (error) {
          clearTimeout(timeout);
          reject(new Error('Image compression failed: ' + error.message));
        }
      };

      img.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('Failed to load image for compression'));
      };

      img.src = imageBase64;
    });
  },

  /**
   * Analyze skin image using secure server-side AI (with client-side compression)
   * @param {string} imageBase64 - Base64 encoded image
   * @param {Object} userInfo - User information for context
   * @returns {Promise<Object>} Analysis result
   */
  analyzeImage: async (imageBase64, userInfo = {}) => {
    // HIPAA Compliance: Removed console logging
const requestFunction = async () => {
      // Compress image first to ensure it's under 6MB
      let imageData = imageBase64;
      if (!imageData.startsWith('data:')) {
        imageData = `data:image/jpeg;base64,${imageBase64}`;
      }

      // HIPAA Compliance: Removed console logging
const compressedImage = await aiAnalysisService.compressImage(imageData, 6, 0.85);

      // Detect if this is a doctor-specific page submission
      const currentPath = window.location.pathname;
      let doctorSlug = null;

      // Only detect doctor slug for single-level paths that could be doctor names
      // Exclude known app routes
      if (currentPath !== '/' &&
          !currentPath.includes('/admin') &&
          !currentPath.includes('/privacy') &&
          !currentPath.includes('/terms') &&
          !currentPath.includes('/docs') &&
          !currentPath.includes('/photo-capture') &&
          !currentPath.includes('/patient-info') &&
          !currentPath.includes('/analysis') &&
          !currentPath.includes('/results') &&
          currentPath.length > 1) {

        const pathParts = currentPath.replace('/', '').split('/');
        // Only consider it a doctor slug if it's a single part (no nested routes)
        if (pathParts.length === 1 && pathParts[0].length > 2) {
          doctorSlug = pathParts[0];
        }
      }

      const requestUserInfo = {
        name: userInfo.name || 'Patient',
        age: userInfo.age || 25,
        gender: userInfo.gender || 'not-specified',
        phone: userInfo.phone || undefined,
        city: userInfo.city || undefined,
        state: userInfo.state || undefined,
        primaryConcern: userInfo.primaryConcern || undefined,
        visitPurpose: userInfo.visitPurpose || undefined,
        conditionDuration: userInfo.conditionDuration || undefined
      };

      // DEBUG: Log what we're sending

      const response = await apiClient.post(`/api/skin-analysis`, {
        imageData: compressedImage,
        userInfo: requestUserInfo,
        doctorSlug: doctorSlug
      });
      return response.data;
    };

    // Re-enable retry mechanism to fix first-attempt failures
    return retryRequest(requestFunction);
  }
};

/**
 * Analytics Service for tracking user interactions
 */
export const analyticsService = {
  // HIPAA-safe: disable tracking endpoints (no network, no PII)
  trackDoctorRecommendation: async () => ({ success: true, disabled: true }),
  trackWhatsAppBooking: async () => ({ success: true, disabled: true })
};

/**
 * Partner Doctor Database Service (MongoDB)
 */
export const doctorService = {
  /**
   * Find nearby partner doctors with analytics tracking
   * @param {number} latitude - User latitude
   * @param {number} longitude - User longitude
   * @param {number} radius - Search radius in km
   * @param {Object} patientInfo - Patient info for tracking
   * @returns {Promise<Array>} List of nearby partner doctors
   */
  findNearbyDoctors: async (latitude, longitude, radius = config.doctors.searchRadius, patientInfo = {}) => {
    const requestFunction = async () => {
      // MongoDB geospatial query for nearby doctors
      const response = await apiClient.post(`${config.doctors.databaseUrl}/api/doctors/nearby`, {
        location: {
          latitude: latitude,
          longitude: longitude
        },
        radius: radius * 1000, // Convert km to meters for MongoDB
        limit: config.doctors.maxResults,
        specialization: 'dermatology',
        isActive: true,
        isPartner: true
      });

      // Map response; no tracking or PII propagation
      const doctorsWithCommission = response.data.doctors.map(doctor => ({
        ...doctor,
        commissionRate: doctor.commission_rate || config.doctors.commissionRate,
        isPartner: true,
        partnershipLevel: doctor.partnership_level || 'standard'
      }));

      return {
        success: true,
        data: doctorsWithCommission,
        totalCount: response.data.totalCount || 0,
        source: 'partner_database'
      };
    };

    return retryRequest(requestFunction);
  },

  /**
   * Get partner doctor details by ID
   * @param {string} doctorId - Doctor ID
   * @returns {Promise<Object>} Doctor details
   */
  getDoctorDetails: async (doctorId) => {
    const requestFunction = async () => {
      const response = await apiClient.get(`${config.doctors.databaseUrl}/api/doctors/${doctorId}`);
      
      return {
        success: true,
        data: {
          ...response.data,
          commissionRate: response.data.commission_rate || config.doctors.commissionRate,
          isPartner: true
        }
      };
    };

    return retryRequest(requestFunction);
  },

  /**
   * Track appointment booking for commission
   * @param {string} doctorId - Doctor ID
   * @param {Object} patientInfo - Patient information
   * @param {Object} analysisInfo - Analysis results
   * @returns {Promise<Object>} Booking tracking result
   */
  trackAppointmentBooking: async (doctorId, patientInfo, analysisInfo) => {
    const requestFunction = async () => {
      const response = await apiClient.post(`${config.doctors.databaseUrl}/api/bookings/track`, {
        doctorId: doctorId,
        patientName: patientInfo.name,
        patientPhone: patientInfo.phone,
        analysisCondition: analysisInfo.condition,
        analysisSeverity: analysisInfo.severity,
        bookingSource: 'myskinai_app',
        timestamp: new Date().toISOString(),
        commissionRate: config.doctors.commissionRate,
        estimatedFee: analysisInfo.severity === 'Severe' ? 2000 : 
                     analysisInfo.severity === 'Moderate' ? 1500 : 1000 // Estimated consultation fees
      });

      return {
        success: true,
        data: response.data,
        bookingId: response.data.bookingId
      };
    };

    return retryRequest(requestFunction);
  }
};



/**
 * WhatsApp API Service
 */
export const whatsappService = {
  /**
   * Generate WhatsApp booking URL
   * @param {Object} doctorInfo - Doctor information
   * @param {Object} patientInfo - Patient information
   * @param {Object} analysisInfo - AI analysis results
   * @returns {string} WhatsApp URL
   */
  generateBookingUrl: (doctorInfo, patientInfo, analysisInfo) => {
    const template = config.whatsapp.messageTemplate;
    
    const message = [
      template.greeting,
      template.referral,
      '',
      template.patientInfo.replace('{patientName}', patientInfo.name),
      template.analysisInfo
        .replace('{condition}', analysisInfo.condition)
        .replace('{severity}', analysisInfo.severity),
      '',
      template.request
    ].join('\n');

    const phoneNumber = doctorInfo.whatsapp_number || doctorInfo.phone;
    const cleanPhoneNumber = phoneNumber.replace(/[^\d]/g, '');
    
    const encodedMessage = encodeURIComponent(message);
    
    return `https://wa.me/${cleanPhoneNumber}?text=${encodedMessage}`;
  },

  /**
   * Open WhatsApp with pre-filled message and track booking
   * @param {string} url - WhatsApp URL
   * @param {string} doctorId - Doctor ID for tracking
   * @param {Object} patientInfo - Patient information
   * @param {Object} analysisInfo - Analysis results
   */
  openWhatsApp: async (url, doctorId = null, patientInfo = {}, analysisInfo = {}) => {
    // Track the WhatsApp redirect for analytics
    if (doctorId && patientInfo.name && config.features.analytics) {
      try {
        await apiClient.post('/api/track-booking', {
          doctorId: doctorId,
          patientInfo: patientInfo,
          analysisInfo: analysisInfo
        });
      } catch (error) {
        }
    }
    
    // Open WhatsApp
    window.open(url, '_blank');
  }
};

/**
 * Generic error handler
 */
export const handleApiError = (error) => {
  if (error.response) {
    // Server responded with error status
    const status = error.response.status;
    const message = error.response.data?.message || error.message;
    
    switch (status) {
      case 400:
        return 'Invalid request. Please check your input and try again.';
      case 401:
        return 'Authentication failed. Please check your API credentials.';
      case 403:
        return 'Access forbidden. You do not have permission to perform this action.';
      case 404:
        return 'Resource not found. Please verify the request.';
      case 429:
        return 'Too many requests. Please wait a moment and try again.';
      case 500:
        return 'Server error. Please try again later.';
      default:
        return message || 'An unexpected error occurred.';
    }
  } else if (error.request) {
    // Network error
    return config.errors.network;
  } else {
    // Other error
    return error.message || 'An unexpected error occurred.';
  }
};

export default apiClient;
